import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/* --------------------------------------------------------------------------
   Card variants
   -------------------------------------------------------------------------- */

const cardVariants = cva(
  [
    'bg-surface-card rounded-[var(--radius-lg)]',
    'border border-border-default',
    'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
  ].join(' '),
  {
    variants: {
      shadow: {
        none: '',
        sm: 'shadow-sm',
        md: 'shadow-md',
      },
      padding: {
        none: '',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
      interactive: {
        true: 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm',
        false: '',
      },
    },
    defaultVariants: {
      shadow: 'sm',
      padding: 'md',
      interactive: false,
    },
  },
);

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /** Optional header area — rendered with a bottom border separator. */
  header?: ReactNode;
  /** Optional footer area — rendered with a top border separator. */
  footer?: ReactNode;
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      shadow,
      padding,
      interactive,
      header,
      footer,
      children,
      ...props
    },
    ref,
  ) => {
    const hasSections = Boolean(header) || Boolean(footer);

    return (
      <div
        ref={ref}
        className={cn(
          cardVariants({
            shadow,
            padding: hasSections ? 'none' : padding,
            interactive,
          }),
          className,
        )}
        {...props}
      >
        {/* Header */}
        {header && (
          <div className="border-b border-border-default px-6 py-4">
            {header}
          </div>
        )}

        {/* Body */}
        <div className={hasSections ? 'px-6 py-5' : undefined}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-border-default px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    );
  },
);

Card.displayName = 'Card';

export { cardVariants };
